/**
 * Inline SVG icons for the HUD (no external assets). All weapon icons share a 64x32 viewBox,
 * muzzle pointing right, single fill (inherits `fill` from CSS).
 */
import type { WeaponId } from './types';

const W = (body: string) => `<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${body}</svg>`;

export const WEAPON_ICONS: Record<WeaponId, string> = {
  // clenched fist seen from the side: palm block, four knuckles on top, thumb tucked in front
  fist: W(
    `<path d="M17 15h27a4 4 0 0 1 4 4v7a4 4 0 0 1-4 4H17a4 4 0 0 1-4-4v-7a4 4 0 0 1 4-4z"/>` +
      `<circle cx="20" cy="12" r="4.4"/><circle cx="28" cy="10.5" r="4.6"/><circle cx="36.5" cy="10.5" r="4.6"/><circle cx="44.5" cy="12" r="4.2"/>` +
      `<path d="M13 18c-5 0-7 4-6 8h7z"/><path d="M46 17h8l6 4-6 4h-8z" opacity=".9"/>`,
  ),
  // baseball bat: knob, thin handle, tapered barrel
  bat: W(
    `<path d="M4 12.6h9.5L50 8.2c8-1 12 1.5 12 7.8s-4 8.8-12 7.8L13.5 19.4H4a2.6 2.6 0 0 1 0-6.8z"/>` +
      `<path d="M4 11.2h3.2v9.6H4a3 3 0 0 1-3-3v-3.6a3 3 0 0 1 3-3z"/>`,
  ),
  // pistol: slide with rear/front sights, short barrel, angled grip, trigger guard
  pistol: W(
    `<path d="M10 9h44a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3z"/>` +
      `<path d="M55 11h6v4h-6z"/><path d="M11 6h4v3h-4zM48 6h3v3h-3z"/>` +
      `<path d="M15 21h13l-4 10H11z"/>` +
      `<path d="M29 21h9v2h-2.5v3.5H31V23h-2z"/>`,
  ),
  // smg: compact receiver, folding wire stock, box magazine, pistol grip, foregrip
  smg: W(
    `<path d="M15 9h32a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H15a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z"/>` +
      `<path d="M49 12h13v4H49z"/><path d="M40 8h4v2h-4z"/>` +
      `<path d="M3 11h11v3H6v6H3z"/>` +
      `<path d="M27 20h8v11h-8z"/><path d="M17 20h8l-2 10h-8z"/><path d="M41 20h5v6h-5z"/>`,
  ),
  // shotgun: long barrel + tube, pump, angled wooden stock
  shotgun: W(
    `<path d="M19 10h20v9H19z"/><path d="M39 11h24v3H39z"/><path d="M39 15.5h21v3H39z"/>` +
      `<path d="M43 19h11v4H43z"/>` +
      `<path d="M2 12h17v7h-8l-8 6H2z"/>` +
      `<path d="M23 19h6v5h-6z"/>`,
  ),
  // assault rifle: receiver, barrel with front sight, handguard, stock, grip, curved magazine
  rifle: W(
    `<path d="M18 9h27v10H18z"/><path d="M45 12h18v4H45z"/><path d="M55 8h2v4h-2z"/><path d="M45 17h11v3H45z"/>` +
      `<path d="M2 10h16v8h-9l-7 5z"/>` +
      `<path d="M22 19h7l-3 10h-7z"/><path d="M31 19h10l-3.5 11h-10z"/>`,
  ),
};

export const STAR_SVG = `<svg class="h-star" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 1.8l3 6.7 7.2.8-5.4 4.9 1.5 7.2L12 17.8 5.7 21.4l1.5-7.2L1.8 9.3 9 8.5z"/></svg>`;

/** 270-degree speedometer arc (viewBox 0 0 120 120, radius 50, from 135deg clockwise to 45deg; length 235.6). */
export const SPEEDO_SVG = `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<defs><linearGradient id="hSpeedGrad" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stop-color="#ffffff"/><stop offset=".55" stop-color="#ffd166"/><stop offset="1" stop-color="#ff3d81"/></linearGradient></defs>
<path class="arc-bg" d="M24.64 95.36A50 50 0 1 1 95.36 95.36"/>
<path class="arc-bg2" d="M24.64 95.36A50 50 0 1 1 95.36 95.36"/>
<path class="arc-fg" d="M24.64 95.36A50 50 0 1 1 95.36 95.36"/>
</svg>`;

export const KEY = (k: string) => `<kbd class="h-key">${k}</kbd>`;

export const CONTROLS_LIST: { keys: string[]; what: string }[] = [
  { keys: ['W', 'A', 'S', 'D'], what: 'Move' },
  { keys: ['Mouse'], what: 'Look' },
  { keys: ['Shift'], what: 'Sprint' },
  { keys: ['Space'], what: 'Jump / Handbrake' },
  { keys: ['LMB'], what: 'Fire' },
  { keys: ['RMB'], what: 'Aim' },
  { keys: ['E'], what: 'Enter / exit vehicle' },
  { keys: ['1', '-', '6'], what: 'Weapons' },
  { keys: ['Scroll'], what: 'Switch weapon' },
  { keys: ['H'], what: 'Horn' },
  { keys: ['R'], what: 'Radio' },
  { keys: ['V'], what: 'Camera' },
  { keys: ['Esc'], what: 'Pause' },
];

export function controlsHtml(list = CONTROLS_LIST): string {
  return list
    .map((c) => `<div><span class="keys">${c.keys.map((k) => (k === '-' ? '<span style="opacity:.6">to</span>' : KEY(k))).join('')}</span><span>${c.what}</span></div>`)
    .join('');
}
